import BoxImage from "@components/BoxImage";
import BoxVideoThumbnail from "@components/BoxVideoThumbnail";
import FileInput from "@components/EasyDev/form/FileInput";
import ModalConfirmation from "@components/ModalConfirmation";
import { EUiStates } from "@constants/enum";
import { removeFileFromFileList } from "@helpers/createMedia.helper";
import { useEffect, useState } from "react";
import { Field, useField, useForm } from "react-final-form";
import GalleryPreview from "../GalleryPreview";
import ImagePreview from "../ImagePreview";
import styles from "./FileUpload.module.scss";
import { isYoutubeUrl } from "@helpers/youtube.helper";

interface IFileUpload {
  title?: string;
  maxFiles?: number;
  isSingle?: boolean;
  initialData?: any;
  formFieldName?: string;
}

const FileUpload = ({
  title,
  maxFiles = 10,
  isSingle = false,
  initialData,
  formFieldName,
}: IFileUpload) => {
  const form = useForm();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [fileInputKey, setFileInputKey] = useState(Date.now());
  const fieldName = formFieldName
    ? formFieldName
    : title
    ? title?.toLowerCase()
    : "Media";
  const maxFilesAllowed = !isSingle ? maxFiles : 1;
  const [mediaPreviews, setMediaPreviews] = useState<
    { url: string; type: string }[]
  >([]);

  // Check if it's Media Gallery Single array or simple mediaUrl, and youtube link
  const isYoutube =
    (Array.isArray(initialData?.[fieldName]) &&
      isYoutubeUrl(initialData?.[fieldName][0].mediaUrl)) ||
    isYoutubeUrl(initialData?.[fieldName]);

  // Track gallery input value
  const { input } = useField(fieldName, { subscription: { value: true } });

  const handleFilesChange = (files: FileList | null) => {
    if (!files) return;

    const fileArray = Array.from(files);
    if (fileArray.length > maxFilesAllowed) {
      setIsModalOpen(true);
      form.change(fieldName, null);

      // Force input re-render
      setFileInputKey(Date.now());
      return;
    }

    // Generate previews for images and videos
    const previews = fileArray.map((file) => {
      const url = URL.createObjectURL(file);
      return {
        url,
        type: file.type.startsWith("image") ? "image" : "video",
      };
    });

    setMediaPreviews(previews);
  };

  const handleOnRemove = (mediaUrl: string) => {
    const mediaIndex = mediaPreviews.findIndex(
      (media) => media.url === mediaUrl
    );
    const newFileList = removeFileFromFileList(
      input.value,
      input.value[mediaIndex]
    );
    form.change(fieldName, newFileList);
    setMediaPreviews((prevState) =>
      prevState.filter((_item, index) => index !== mediaIndex)
    );
  };

  useEffect(() => {
    if (input.value instanceof FileList) {
      handleFilesChange(input.value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input.value]);

  return (
    <>
      <ModalConfirmation
        icon="plus-circle"
        title={`Files | Warning`}
        body={`You can only upload up to ${maxFilesAllowed} files.`}
        handleConfirm={() => setIsModalOpen(!isModalOpen)}
        handleModalStatus={() => setIsModalOpen(!isModalOpen)}
        modalStatus={isModalOpen}
        modalType={EUiStates.WARNING}
      />

      <div className={`${styles.fileUpload} form__form-group`}>
        {title && <span className="form__form-group-label">{title}</span>}
        <div className={`${styles.uploadSection} form__form-group-field`}>
          <Field
            name={fieldName}
            component={FileInput}
            isAboveError
            multiple={!isSingle}
            key={fileInputKey}
            initialValue={
              !Array.isArray(initialData?.[fieldName])
                ? initialData?.[fieldName]
                : Array.isArray(initialData?.[fieldName])
                ? initialData?.[fieldName][0].mediaUrl
                : ""
            }
          />
        </div>
        <div className={styles.bottomSection}>
          <span className={styles.sizeMessage}>
            Supported file types: JPG, PNG or GIF. Max size: 10MB.
          </span>
          <div className={styles.preview}>
            {initialData && !isSingle && !isYoutube && (
              <GalleryPreview data={initialData} form={form} />
            )}

            {initialData && formFieldName && isSingle && !isYoutube && (
              <ImagePreview
                url={initialData[formFieldName]}
                form={form}
                fieldName={formFieldName}
              />
            )}

            {mediaPreviews.map((media) => (
              <div key={media.url} className={styles.mediaPreview}>
                {media.type === "image" ? (
                  <BoxImage
                    urlImage={media.url}
                    isPreview
                    onRemove={() => handleOnRemove(media.url)}
                  />
                ) : (
                  <BoxVideoThumbnail
                    url={media.url}
                    onRemove={() => handleOnRemove(media.url)}
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default FileUpload;
